import { PlusCircleOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDrop } from 'react-dnd';
import styled from 'styled-components';

import { Flex, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import Typo from '@/components/Typography/Typo';
import { useFeedbackTerminology } from '@/entities/workspace';
import {
  FEEDBACK_GALLERY_DEFAULT_TITLE,
  getDefaultFilterSet,
  getFeedbackGalleryTitle,
} from '@/features/(dashboard)/feedback-gallery/model/constants';
import {
  DASHBOARD_ROW_MAX_HEIGHT,
  DASHBOARD_ROW_MIN_HEIGHT,
  Dashboard2ColumnType,
} from '@/features/Dashboard/types/constants';
import type {
  Dashboard2DnDColumnItemT,
  Dashboard2DnDItemT,
  MappedChartRow,
} from '@/features/Dashboard/types/types';
import useColor from '@/hooks/domain/useColor';
import service from '@/services/index';
import useDashboard2Store from '@/stores/Dashboard2Store';
import usePopupStore from '@/stores/pure/PopupStore';
import { useToast } from '@/stores/pure/ToastStore';

import AddChartDashboardMenu from './AddChartDashboardMenu/AddChartDashboardMenu';
import { trimTrailingEmptyQuillBlocks } from './DashboardCardContent/quill-html';
import SimpleEditor from './DashboardCardContent/SimpleEditor';
import { DashboardTextContentStyle } from './styles/TextContentStyle';

type DashboardRowEmpty = {
  rowIndex: number;
  dashboardId: string;
  someDragging: boolean;
  rowList: MappedChartRow[];
  currentCharts: string[];
  rowId?: string;
};

const DashboardRowHandler = (props: DashboardRowEmpty) => {
  const initialTitleText = '<h1>Section Title</h1>';
  const initialDescriptionText = '<p>Section Description</p>';
  const color = useColor();
  const { replaceFeedbackTerminology } = useFeedbackTerminology();
  const queryClient = useQueryClient();
  const [isEditingTitle, setisEditingTitle] = useState(false);
  const [title, setTitle] = useState(initialTitleText);
  const [isResizing, setIsResizing] = useState<HTMLDivElement | null>(null);
  const startResizeY = useRef(0);
  const startHeight = useRef(0);
  const { setPopup } = usePopupStore((state) => ({ setPopup: state.setPopup }));
  const addToast = useToast();
  const { setEditingState, setPendingFeedbackGallery } = useDashboard2Store(
    (state) => ({
      setEditingState: state.setEditingState,
      clearEditingState: state.clearEditingState,
      setPendingFeedbackGallery: state.setPendingFeedbackGallery,
    }),
  );

  const handleEditMode = (state: boolean) => {
    setisEditingTitle(state);
    setEditingState(state);
  };

  const allowColumnTypes = [
    String(Dashboard2ColumnType.DASHBOARD_COLUMN_CHART),
    String(Dashboard2ColumnType.DASHBOARD_COLUMN_TEXT_BOX),
    String(Dashboard2ColumnType.DASHBOARD_COLUMN_ANALYSIS),
    String(Dashboard2ColumnType.DASHBOARD_COLUMN_FEEDBACK_GALLERY),
  ];

  const resizable = useMemo(() => {
    const dataTarget = props.rowList.find((row) => row.id === props.rowId);
    if (!dataTarget) return false;
    return true;
  }, [props.rowList, props.rowId]);

  const [{ isOver }, dropZone] = useDrop<
    Dashboard2DnDItemT,
    void,
    { isOver: boolean }
  >({
    accept: allowColumnTypes,
    collect(monitor) {
      const itemType = monitor.getItemType();
      if (typeof itemType !== 'string')
        return {
          isOver: false,
        };
      return {
        isOver: allowColumnTypes.includes(itemType) && monitor.isOver(),
      };
    },
    drop(item, monitor) {
      const type = monitor.getItemType();
      if (typeof type !== 'string') return;

      if (!allowColumnTypes.includes(type)) return;
      onDashboardDnDItemDrop(item);
    },
  });

  const onDashboardDnDItemDrop = async (item: Dashboard2DnDItemT) => {
    if (!item.rowId) return;
    const columnItem = item as Dashboard2DnDColumnItemT;

    const newRow = await service.dashboard2.addDashboardRow(props.dashboardId, {
      pos: props.rowIndex,
      columns: [],
    });

    try {
      await service.dashboard2.moveColumnInDashboardRow(
        props.dashboardId,
        item.rowId,
        columnItem.column.id,
        {
          destinationRowId: newRow.row_id,
          pos: 0,
        },
      );
    } catch (e) {
      console.error(e);
      addToast('Failed to move the column.', 'fail');
      await service.dashboard2.deleteDashboardRow(
        props.dashboardId,
        newRow.row_id,
      );
    }

    refetchDashboard();
  };

  const onClickAddNewTitle = () => {
    handleEditMode(true);
    setTitle(initialTitleText);
  };

  const onClickAddNewDescription = () => {
    handleEditMode(true);
    setTitle(initialDescriptionText);
  };

  const onClickCreateNewFeedbackGallery = () => {
    // 즉시 API 호출 대신, pending 상태로 설정하여 dashboard에 임시 카드 표시
    setPendingFeedbackGallery({
      dashboardId: props.dashboardId,
      rowIndex: props.rowIndex,
      feedbacksSetting: {
        filter_set: getDefaultFilterSet(),
        title: getFeedbackGalleryTitle(
          FEEDBACK_GALLERY_DEFAULT_TITLE,
          replaceFeedbackTerminology,
        ),
        sort_by: 'date',
        order_by: 'desc',
      },
    });
    setEditingState(true);
  };

  const createDashboardRowSection = async () => {
    const nextText = trimTrailingEmptyQuillBlocks(title);

    if (nextText) {
      try {
        await service.dashboard2.addDashboardRow(props.dashboardId, {
          // 실제 row index 로 변경
          pos: props.rowIndex,
          columns: [
            {
              text: nextText,
              type: Dashboard2ColumnType.DASHBOARD_COLUMN_TEXT_BOX,
            },
          ],
          // title,
          // type: DashboardRowType.DASHBOARD_ROW_SECTION,
        });

        // reset Form first to clear editing state
        resetRowTitleForm();

        // then refetch dashboard data
        refetchDashboard();
      } catch (e) {
        setPopup({
          popupTheme: 'negative',
          title: 'Error',
          message: 'Failed to create the section. Please try again later.',
          type: 'alert',
        });
      }
    } else {
      // reset Form
      resetRowTitleForm();
    }
  };

  const refetchDashboard = () => {
    queryClient.invalidateQueries({
      queryKey: ['dashboard2', props.dashboardId],
    });
  };

  const resetRowTitleForm = () => {
    setTitle('');
    handleEditMode(false);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!resizable) return;
    if (!props.rowId) return;
    const target = document.getElementById(props.rowId) as HTMLDivElement;
    if (!target) return;

    setIsResizing(target);
    startResizeY.current = e.clientY;
    startHeight.current = target.getBoundingClientRect().height;
    e.preventDefault();
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;

      const delta = e.clientY - startResizeY.current;
      const clamp = (value: number, min: number, max: number) =>
        Math.min(Math.max(value, min), max);
      const newHeight = clamp(
        Math.floor(startHeight.current + delta),
        DASHBOARD_ROW_MIN_HEIGHT,
        DASHBOARD_ROW_MAX_HEIGHT,
      );
      isResizing.style.height = `${newHeight}px`;
    };

    const handleMouseUp = () => {
      if (!isResizing) return;
      const height = Math.floor(isResizing.getBoundingClientRect().height);
      service.dashboard2.changeDashboardRowHeight(
        props.dashboardId,
        props.rowId!,
        `${height}px`,
      );
      setIsResizing(null);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  return (
    <StyledDashboardRowHandler>
      {isEditingTitle ? (
        <EditorWrapperStyle style={{ width: '100%' }} g={8}>
          <SimpleEditor
            value={title}
            onChange={setTitle}
            wrapperStyle={{
              backgroundColor: color.gray200,
              padding: '8px 24px',
              borderRadius: 16,
              height: '100%',
              maxHeight: 600,
              overflow: 'auto',
              flex: 1,
            }}
            textStyleCss={DashboardTextContentStyle}
          />
          <Flex j="space-between">
            <Flex></Flex>
            <Flex g={8} j="flex-end">
              <Button variant="secondary" onClick={() => handleEditMode(false)}>
                Cancel
              </Button>
              <Button onClick={createDashboardRowSection}>Done</Button>
            </Flex>
          </Flex>
        </EditorWrapperStyle>
      ) : props.someDragging ? (
        <Flex
          ref={dropZone}
          style={{
            height: '22px',
            width: '100%',
          }}
          a="center"
        >
          <Flex
            f={1}
            height="4px"
            style={{
              background: isOver ? color.primary200 : 'transparent',
            }}
          />
        </Flex>
      ) : (
        <HandlerStyle
          g={8}
          a="center"
          ref={dropZone}
          onMouseDown={handleMouseDown}
          style={{
            cursor: resizable ? 'row-resize' : 'default',
          }}
        >
          <AddChartDashboardMenu
            rowIndex={props.rowIndex}
            currentCharts={props.currentCharts}
            actions={{
              onClickCreateNewTitle: onClickAddNewTitle,
              onClickCreateNewDescription: onClickAddNewDescription,
              onClickCreateNewFeedbackGallery: onClickCreateNewFeedbackGallery,
            }}
            anchor={
              <Flex
                g={6}
                a="center"
                style={{
                  cursor: 'pointer',
                  opacity: isOver ? 0 : 1,
                }}
              >
                <PlusCircleOutlined
                  style={{
                    color: color.primary200,
                  }}
                />

                <Typo type="label2" weight="sb" color={color.primary200}>
                  Add
                </Typo>
              </Flex>
            }
          />
          <Flex
            f={1}
            height="4px"
            style={{
              background: color.primary200,
            }}
          />
        </HandlerStyle>
      )}
    </StyledDashboardRowHandler>
  );
};

const StyledDashboardRowHandler = styled.div`
  padding: 0 12px;
`;

const EditorWrapperStyle = styled(VFlex)`
  flex: 1;
  flex-shrink: 1;
  min-height: 160px;
  overflow: auto;
  padding: 16px 0px;
`;

const HandlerStyle = styled(Flex)`
  opacity: 0;
  transition: opacity 0.3s;
  padding: 4px 0;

  height: 22px;
  user-select: none;

  &:hover {
    opacity: 1;
  }

  &:active {
    opacity: 1;
  }
`;
export default DashboardRowHandler;
